import { ref, onBeforeMount } from "vue";
import { useExplorerStore } from "@/modules/explorer/store";
import dayjs from "dayjs";

import { useHotkeys } from "@/ui/hotkeys/useHotkeys";
import { parse } from "chartjs-graph";

type Subject = "start" | "end" | "period";
type Operation = "subtract" | "add";
type Timeframe = "day" | "week" | "month" | "year";
type TemporalScope = "period" | "snapshot";
const unitToTimeframeMap = new Map<string, string>();
unitToTimeframeMap.set("day", "d");
unitToTimeframeMap.set("week", "w");
unitToTimeframeMap.set("month", "M");

export function useExplorerHotkeyBindings() {
	const explorerStore = useExplorerStore();
	const keyIncrement = ref(1);

	const isDynamicDate = (date: string): boolean => {
		return date.includes("now");
	};

	const updateDate = (
		date: string,
		operation: Operation,
		timeframe: Timeframe,
		newDate
	) => {
		if (isDynamicDate(date)) {
			date = performDynamicOperation(date, operation, timeframe);
		} else {
			date = dayjs(newDate).format("YYYY-MM-DD HH:mm:ss");
		}
		return date;
	};

	const stripRounding = (dateString: string) =>
		dateString.replace(/\/[smhdwMQy]/, "");

	const getRounding = (dateString: string) => {
		const match = dateString.match(/\/[smhdwMQy]/);

		if (!match) return null;

		return match;
	};

	const extractLastDynamicUnit = (dateString: string, unit: string) => {
		const withoutRounding = stripRounding(dateString);
		const regex = new RegExp(`([+-]\\d+)(${unit})(?!.*[+-]\\d+${unit})`);
		const match = withoutRounding.match(regex);
		if (!match) return null;

		return {
			value: Number(match[1]),
			unit: match[2],
			start: match.index,
			end: match.index + match[0].length,
		};
	};

	const performDynamicOperation = (
		dateString: string,
		operation: Operation,
		timeframe: Timeframe
	) => {
		const unit = unitToTimeframeMap.get(timeframe);
		if (!unit) return dateString;

		const rounding = getRounding(dateString);

		let before = dateString;
		let base: string;

		if (rounding) {
			const [b, rest] = dateString.split(rounding);
			before = b;
			base = rest ?? "";
		} else {
			before = "";
			base = dateString;
		}

		const lastDynamicUnit = extractLastDynamicUnit(base, unit);
		if (lastDynamicUnit && lastDynamicUnit.unit === unit) {
			base =
				base.slice(0, lastDynamicUnit.start) + base.slice(lastDynamicUnit.end);
			const newValue =
				operation === "add"
					? lastDynamicUnit.value + 1
					: lastDynamicUnit.value - 1;

			if (newValue !== 0) {
				const sign = newValue > 0 ? "+" : "";
				base += `${sign}${newValue}${unit}`;
			}
		} else {
			base += operation === "add" ? `+1${unit}` : `-1${unit}`;
		}

		return rounding ? `${before}${rounding}${base}` : before + base;
	};

	const getUpdatedDate = (
		subject: Subject,
		operation: Operation,
		timeframe: Timeframe,
		temporalScope: TemporalScope = "period"
	) => {
		const explorerStore = useExplorerStore();

		const baseDate =
			temporalScope === "period"
				? explorerStore.document.period_selection[
						subject === "start" ? "from_parsed" : "to_parsed"
				  ]
				: explorerStore.document.period_measurement_parsed;

		return dayjs(baseDate)[operation](1, timeframe).toDate();
	};

	const mutatePeriod = (
		subject: Subject,
		operation: Operation,
		timeframe: Timeframe
	) => {
		const explorerStore = useExplorerStore();

		if (subject === "period") {
			const newStartDate = getUpdatedDate(
				"start",
				operation,
				timeframe,
				"period"
			);
			const newEndDate = getUpdatedDate("end", operation, timeframe, "period");

			if (operation === "add") {
				if (newEndDate >= explorerStore.document.period_selection.from_parsed) {
					explorerStore.document.period_selection.to = updateDate(
						explorerStore.document.period_selection.to,
						operation,
						timeframe,
						newEndDate
					);
					explorerStore.document.period_selection.to_parsed = parse(
						explorerStore.document.period_selection.to,
						true
					).toDate();
				}

				if (newStartDate <= explorerStore.document.period_selection.to_parsed) {
					explorerStore.document.period_selection.from = updateDate(
						explorerStore.document.period_selection.from,
						operation,
						timeframe,
						newStartDate
					);
					explorerStore.document.period_selection.from_parsed = parse(
						explorerStore.document.period_selection.from
					).toDate();
				}
			} else {
				if (newStartDate <= explorerStore.document.period_selection.to_parsed) {
					explorerStore.document.period_selection.from = updateDate(
						explorerStore.document.period_selection.from,
						operation,
						timeframe,
						newStartDate
					);
					explorerStore.document.period_selection.from_parsed = parse(
						explorerStore.document.period_selection.from
					).toDate();
				}

				if (newEndDate >= explorerStore.document.period_selection.from_parsed) {
					explorerStore.document.period_selection.to = updateDate(
						explorerStore.document.period_selection.to,
						operation,
						timeframe,
						newEndDate
					);
					explorerStore.document.period_selection.to_parsed = parse(
						explorerStore.document.period_selection.to,
						true
					).toDate();
				}
			}
		} else {
			const newDate = getUpdatedDate(subject, operation, timeframe);

			if (
				(subject === "start" &&
					newDate <= explorerStore.document.period_selection.to_parsed) ||
				(subject === "end" &&
					newDate >= explorerStore.document.period_selection.from_parsed)
			) {
				explorerStore.document.period_selection[
					subject === "start" ? "from" : "to"
				] = updateDate(
					subject === "start"
						? explorerStore.document.period_selection.from
						: explorerStore.document.period_selection.to,
					operation,
					timeframe,
					newDate
				);
				explorerStore.document.period_selection[
					subject === "start" ? "from_parsed" : "to_parsed"
				] = parse(
					explorerStore.document.period_selection[
						subject === "start" ? "from" : "to"
					],
					subject === "end"
				).toDate();
			}
		}

		keyIncrement.value++;
	};

	const mutateSnapshot = (operation: Operation, timeframe: Timeframe) => {
		explorerStore.explorerState.showMeasureDateTime = true;

		// todo add check date is valid for snapshot selection ( discuss with Sander )

		const newmeasureDateTime = getUpdatedDate(
			"start",
			operation,
			timeframe,
			"snapshot"
		);

		if (newmeasureDateTime <= new Date()) {
			explorerStore.document.period_measurement = updateDate(
				explorerStore.document.period_measurement,
				operation,
				timeframe,
				newmeasureDateTime
			);
			explorerStore.document.period_measurement_parsed = newmeasureDateTime;
		}

		keyIncrement.value++;

		// use debounce to hide snapshot-date?
	};

	type DatePresets = "current-day" | "current-week" | "current-month";
	const setDatePreset = (datePreset: DatePresets) => {
		// if (datePreset === "current-day") {
		// 	explorerStore.document.period_selection.from_parsed = dayjs().toDate();
		// 	explorerStore.document.period_selection.to_parsed = dayjs().toDate();
		// }
		// if (datePreset === "current-week") {
		// 	explorerStore.document.period_selection.from_parsed = dayjs()
		// 		.startOf("week")
		// 		.toDate();
		// 	explorerStore.document.period_selection.to_parsed = dayjs()
		// 		.endOf("week")
		// 		.toDate();
		// }
		// if (datePreset === "current-month") {
		// 	explorerStore.document.period_selection.from_parsed = dayjs()
		// 		.startOf("month")
		// 		.toDate();
		// 	explorerStore.document.period_selection.to_parsed = dayjs()
		// 		.endOf("month")
		// 		.toDate();
		// }
		//
		// keyIncrement.value++;
	};

	const setupHotkeys = () => {
		// Period change
		useHotkeys(() => mutatePeriod("period", "add", "day"), ["ArrowRight"]);
		useHotkeys(() => mutatePeriod("period", "subtract", "day"), ["ArrowLeft"]);

		useHotkeys(() => mutatePeriod("period", "add", "week"), ["."]);
		useHotkeys(() => mutatePeriod("period", "subtract", "week"), [","]);

		useHotkeys(() => mutatePeriod("period", "add", "month"), ["]"]);
		useHotkeys(() => mutatePeriod("period", "subtract", "month"), ["["]);

		// End of period change
		useHotkeys(
			() => mutatePeriod("end", "add", "day"),
			["Shift", "ArrowRight"]
		);
		useHotkeys(
			() => mutatePeriod("end", "subtract", "day"),
			["Shift", "ArrowLeft"]
		);

		useHotkeys(() => mutatePeriod("end", "add", "week"), ["Shift", ">"]);
		useHotkeys(() => mutatePeriod("end", "subtract", "week"), ["Shift", "<"]);

		useHotkeys(() => mutatePeriod("end", "add", "month"), ["Shift", "}"]);
		useHotkeys(() => mutatePeriod("end", "subtract", "month"), ["Shift", "{"]);

		// Begin of period change
		useHotkeys(
			() => mutatePeriod("start", "add", "day"),
			["Shift", "Ctrl", "ArrowRight"]
		);
		useHotkeys(
			() => mutatePeriod("start", "subtract", "day"),
			["Shift", "Ctrl", "ArrowLeft"]
		);

		useHotkeys(
			() => mutatePeriod("start", "add", "week"),
			["Shift", "Ctrl", ">"]
		);
		useHotkeys(
			() => mutatePeriod("start", "subtract", "week"),
			["Shift", "Ctrl", "<"]
		);

		useHotkeys(
			() => mutatePeriod("start", "add", "month"),
			["Shift", "Ctrl", "}"]
		);
		useHotkeys(
			() => mutatePeriod("start", "subtract", "month"),
			["Shift", "Ctrl", "{"]
		);

		// snapshot
		useHotkeys(() => mutateSnapshot("add", "day"), ["Alt", "ArrowRight"]);
		useHotkeys(() => mutateSnapshot("subtract", "day"), ["Alt", "ArrowLeft"]);

		useHotkeys(() => mutateSnapshot("add", "week"), ["Alt", "≥"]);
		useHotkeys(() => mutateSnapshot("subtract", "week"), ["Alt", "≤"]);

		useHotkeys(() => mutateSnapshot("add", "month"), ["Alt", "‘"]);
		useHotkeys(() => mutateSnapshot("subtract", "month"), ["Alt", "“"]);

		// Predefined periods
		useHotkeys(() => setDatePreset("current-day"), ["Ctrl", "d"]);
		useHotkeys(() => setDatePreset("current-week"), ["Ctrl", "w"]);
		useHotkeys(() => setDatePreset("current-month"), ["Ctrl", "m"]);
	};

	setupHotkeys();

	return {
		keyIncrement,
		setDatePreset,
	};
}
